import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import IMAGES from '../lib/images';
import Icon from './Icon';

// The route whose HTML was prerendered, if any. Whatever is on screen there has already been painted
// by the time this code runs, so animating it in would make visible content blink out and back.
const PAINTED_PATH = document.getElementById('root')?.hasChildNodes() ? window.location.pathname : null;

// Plays an entrance once, when the element reaches the viewport. Content is never hidden beforehand,
// so nothing is lost if the observer doesn't fire or the page is captured in full.
export function useInView() {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === 'undefined') return undefined;
    let firstCheck = true;
    const io = new IntersectionObserver(
      ([entry]) => {
        const alreadyPainted = firstCheck && window.location.pathname === PAINTED_PATH;
        firstCheck = false;
        if (entry.isIntersecting) {
          if (!alreadyPainted) el.classList.add('is-in');
          io.disconnect();
        }
      },
      { rootMargin: '0px 0px -6% 0px' }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return ref;
}

export function Reveal({ as: Tag = 'div', delay = 0, className = '', style, children, ...rest }) {
  const ref = useInView();
  return (
    <Tag
      ref={ref}
      className={`reveal ${className}`}
      style={delay ? { '--delay': `${delay}ms`, ...style } : style}
      {...rest}
    >
      {children}
    </Tag>
  );
}

// A hairline that draws itself in. tone="bronze" is for the moments that should mean something.
export function Hairline({ tone = 'light', className = '' }) {
  const ref = useInView();
  const colour = { light: 'bg-stone', dark: 'bg-limestone/20', bronze: 'bg-bronze', wall: 'bg-wall' }[tone];
  return <div ref={ref} aria-hidden="true" className={`hairline h-px w-full ${colour} ${className}`} />;
}

const TONES = {
  limestone: 'bg-limestone text-wall',
  white: 'bg-white text-wall',
  stone: 'bg-stone text-wall',
  tint: 'bg-glass-tint text-wall',
  wall: 'on-dark bg-gradient-to-b from-wall to-spandrel text-limestone',
  'wall-flat': 'on-dark bg-wall text-limestone',
};

export function Mullions({ dark = false }) {
  return <div aria-hidden="true" className={`mullions ${dark ? 'mullions-dark' : 'mullions-light'}`} />;
}

export function Section({ tone = 'limestone', pad = 'lg', id, className = '', mullions = true, children, ...rest }) {
  const padding = { lg: 'section-y', sm: 'section-y-sm', none: '' }[pad];
  return (
    <section id={id} className={`relative ${TONES[tone]} ${padding} ${className}`} {...rest}>
      {mullions && <Mullions dark={tone.startsWith('wall')} />}
      <div className="relative">{children}</div>
    </section>
  );
}

export function Label({ children, dark = false, muted = false, as: Tag = 'span', className = '' }) {
  const colour = muted
    ? dark
      ? 'text-glass-light'
      : 'text-glass'
    : dark
    ? 'text-bronze-light'
    : 'text-bronze-deep';
  return <Tag className={`label block ${colour} ${className}`}>{children}</Tag>;
}

// layout="split": title left, lede far right (the default).
// layout="offset": a wide title, with the lede set in from the left beneath it.
// layout="statement": a centred statement, for the moments a section should stop the eye.
export function SectionHead({ label, title, aside, dark = false, as: Tag = 'h2', layout = 'split', className = '' }) {
  const asideColour = dark ? 'text-glass-light' : 'text-glass';
  if (layout === 'statement') {
    return (
      <header className={`mx-auto max-w-[940px] lg:text-center ${className}`}>
        {label && <Label dark={dark} className="mb-5">{label}</Label>}
        <Tag className="type-h1-compact">{title}</Tag>
        {aside && <p className={`type-lead mt-6 lg:mx-auto ${asideColour}`}>{aside}</p>}
      </header>
    );
  }
  if (layout === 'offset') {
    return (
      <header className={`grid-12 gap-y-6 ${className}`}>
        <div className="col-span-4 lg:col-span-10">
          {label && <Label dark={dark} className="mb-5">{label}</Label>}
          <Tag className="type-h1-compact">{title}</Tag>
        </div>
        {aside && <p className={`type-lead col-span-4 lg:col-span-6 lg:col-start-6 ${asideColour}`}>{aside}</p>}
      </header>
    );
  }
  return (
    <header className={`grid-12 gap-y-6 items-end ${className}`}>
      <div className="col-span-4 lg:col-span-7">
        {label && <Label dark={dark} className="mb-5">{label}</Label>}
        <Tag className="type-h2">{title}</Tag>
      </div>
      {aside && (
        <p className={`col-span-4 lg:col-span-4 lg:col-start-9 type-body ${dark ? 'text-glass-light' : 'text-glass'}`}>
          {aside}
        </p>
      )}
    </header>
  );
}

export function ArchImage({ name, alt = '', priority = false, className = '', imgClassName = '', sizes = '100vw', settle = true }) {
  const ref = useInView();
  const img = IMAGES[name];
  return (
    <div ref={ref} className={`overflow-hidden bg-glass-tint ${settle ? 'settle' : ''} ${className}`}>
      <img
        src={img.src}
        width={img.width}
        height={img.height}
        sizes={sizes}
        alt={alt}
        loading={priority ? 'eager' : 'lazy'}
        decoding="async"
        fetchPriority={priority ? 'high' : 'auto'}
        className={`block h-full w-full object-cover ${imgClassName}`}
      />
    </div>
  );
}

export function ArrowLink({ to, href, children, className = '', onClick }) {
  const inner = (
    <>
      <span>{children}</span>
      <Icon name="arrow" size={16} />
    </>
  );
  const cls = `link-rule ${className}`;
  if (to) {
    return (
      <Link to={to} className={cls} onClick={onClick}>
        {inner}
      </Link>
    );
  }
  return (
    <a href={href} className={cls} onClick={onClick}>
      {inner}
    </a>
  );
}

// A sticky heading rail beside its content, instead of a heading stacked above it.
export function Rail({ label, title, aside, dark = false, children, className = '' }) {
  return (
    <div className={`grid-12 gap-y-10 ${className}`}>
      <header className="col-span-4 lg:sticky lg:top-[112px] lg:col-span-4 lg:self-start">
        {label && <Label dark={dark} className="mb-5">{label}</Label>}
        <h2 className="type-h2">{title}</h2>
        {aside && <p className={`type-body mt-5 ${dark ? 'text-glass-light' : 'text-glass'}`}>{aside}</p>}
      </header>
      <div className="col-span-4 min-w-0 lg:col-span-7 lg:col-start-6">{children}</div>
    </div>
  );
}

// Full-width ruled rows, not cards. `aside` sits right of the detail: a value, or a link.
export function RuledRows({ items, dark = false, titleClass = 'type-h3' }) {
  const rule = dark ? 'border-limestone/20' : 'border-wall/20';
  return (
    <ol className={`m-0 list-none border-b p-0 ${rule}`}>
      {items.map((item) => (
        <Reveal
          as="li"
          key={item.title}
          className={`grid-12 gap-y-2 border-t py-6 lg:py-8 ${rule}`}
        >
          <div className="col-span-4 lg:col-span-5">
            <h3 className={titleClass}>{item.title}</h3>
          </div>
          <p className={`col-span-4 lg:col-span-5 ${dark ? 'text-glass-light' : 'text-glass'}`}>{item.detail}</p>
          {item.to && (
            <div className="col-span-4 lg:col-span-2 lg:text-right">
              <ArrowLink to={item.to}>{item.linkLabel || 'Learn more'}</ArrowLink>
            </div>
          )}
        </Reveal>
      ))}
    </ol>
  );
}

// The annual-report spread: facts set in columns under hairlines, each with a quiet note number.
export function RuledSpread({ items, cols = 2, dark = false, notes = false }) {
  const rule = dark ? 'border-limestone/25' : 'border-wall/25';
  const grid = { 2: 'md:grid-cols-2', 3: 'md:grid-cols-3', 4: 'md:grid-cols-2 xl:grid-cols-4' }[cols];
  return (
    <dl className={`m-0 grid grid-cols-1 gap-x-[clamp(24px,4vw,64px)] gap-y-10 lg:gap-y-14 ${grid}`}>
      {items.map((item, i) => (
        <Reveal key={item.title} delay={i * 50} className={`border-t pt-5 ${rule}`}>
          {notes && (
            <Label muted dark={dark} className="mb-6 figure">
              Note {i + 1}
            </Label>
          )}
          <dt className="type-h3 mb-3">{item.title}</dt>
          <dd className={`m-0 max-w-[46ch] ${dark ? 'text-glass-light' : 'text-glass'}`}>
            {item.detail}
            {item.to && (
              <span className="mt-2 block">
                <ArrowLink to={item.to} className={dark ? 'text-limestone' : 'text-wall'}>
                  {item.linkLabel || 'Learn more'}
                </ArrowLink>
              </span>
            )}
          </dd>
        </Reveal>
      ))}
    </dl>
  );
}
